import { animate, inView, prefersReducedMotion, stagger } from 'motion';

import './icons-with-text.css';

const SECTION_SELECTOR = '.icons-with-text';
const GRID_SELECTOR = '[data-animate-state]';
const CARD_SELECTOR = '[data-animate-card]';
const ITEM_SELECTOR = '[data-animate-item]';

const CARD_DURATION_SEC = 0.5;
const CARD_STAGGER_SEC = 0.2;
const CARD_Y_OFFSET_PX = 16;
const ITEM_DURATION_SEC = 0.8;
const ITEM_STAGGER_SEC = 0.04;
const ITEM_Y_OFFSET_PX = '15%';
const ITEM_DELAY_AFTER_CARD_SEC = 0.2;
const IN_VIEW_MARGIN = '0px 0px -10% 0px';

const stopMap = new WeakMap<HTMLElement, () => void>();

function resetElement(el: HTMLElement): void {
  el.getAnimations().forEach((a) => a.cancel());
  el.style.opacity = '';
  el.style.transform = '';
}

export function initSection(root: HTMLElement): void {
  const grid = root.querySelector<HTMLElement>(GRID_SELECTOR);
  if (!grid) return;

  const previousStop = stopMap.get(grid);
  if (previousStop) {
    previousStop();
    stopMap.delete(grid);
  }

  const cards = Array.from(grid.querySelectorAll<HTMLElement>(CARD_SELECTOR));
  cards.forEach((card) => {
    resetElement(card);
    card.querySelectorAll<HTMLElement>(ITEM_SELECTOR).forEach(resetElement);
  });

  grid.dataset.animateState = 'pending';

  if (prefersReducedMotion.current === true || cards.length === 0) {
    grid.dataset.animateState = 'done';
    return;
  }

  let stop: () => void = () => {};
  stop = inView(
    grid,
    () => {
      stop();
      stopMap.delete(grid);

      const cardDelay = stagger(CARD_STAGGER_SEC, { from: 'first' });
      const pending: Promise<unknown>[] = [];

      cards.forEach((card, i) => {
        const baseDelay = cardDelay(i, cards.length);
        pending.push(
          animate(
            card,
            { opacity: [0, 1], y: [CARD_Y_OFFSET_PX, 0] },
            { duration: CARD_DURATION_SEC, delay: baseDelay, ease: 'easeOut' },
          ).finished,
        );

        const items = Array.from(card.querySelectorAll<HTMLElement>(ITEM_SELECTOR));
        if (items.length === 0) return;
        const itemDelay = stagger(ITEM_STAGGER_SEC, { from: 'first' });
        items.forEach((item, j) => {
          pending.push(
            animate(
              item,
              { opacity: [0, 1], y: [ITEM_Y_OFFSET_PX, 0] },
              {
                duration: ITEM_DURATION_SEC,
                delay: baseDelay + ITEM_DELAY_AFTER_CARD_SEC + itemDelay(j, items.length),
                ease: 'easeOut',
              },
            ).finished,
          );
        });
      });

      Promise.allSettled(pending).then(() => {
        grid.dataset.animateState = 'done';
      });
    },
    { margin: IN_VIEW_MARGIN },
  );

  stopMap.set(grid, stop);
}

document.querySelectorAll<HTMLElement>(SECTION_SELECTOR).forEach(initSection);
